import React, { useEffect, useState } from "react";
import {
  FaChartLine,
  FaSpinner,
  FaTrophy,
  FaUsers,
  FaClipboardList,
  FaCheckCircle,
  FaTimesCircle,
  FaBook,
  FaHeadphones,
  FaPencilAlt,
} from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type Statistics = {
  totalExamSets: number;
  totalAssignments: number;
  totalAttempts: number;
  totalStudents: number;
  passedAttempts: number;
  failedAttempts: number;
  passRate: number;
  avgScore: number;
  levelStats: {
    level: string;
    attempts: number;
    passed: number;
    failed: number;
    passRate: number;
    avgScore: number;
  }[];
  moduleStats: {
    lesen: {
      totalScore: number;
      totalMax: number;
      avgPercentage: number;
    };
    horen: {
      totalScore: number;
      totalMax: number;
      avgPercentage: number;
    };
    schreiben: {
      totalScore: number;
      totalMax: number;
      avgPercentage: number;
    };
  };
  recentAttempts: {
    id: number;
    studentName: string;
    examSetName: string;
    level: string;
    score: number;
    maxScore: number;
    percentage: number;
    isPassed: boolean;
    submittedAt: string;
  }[];
  topPerformers: {
    studentName: string;
    examSetName: string;
    level: string;
    score: number;
    maxScore: number;
    percentage: number;
    submittedAt: string;
  }[];
};

const AdminExamStatistics: React.FC = () => {
  const [stats, setStats] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/exams/statistics`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        setError("Failed to load statistics");
      }
    } catch (err) {
      console.error("Error fetching statistics:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <FaTimesCircle className="text-red-500 text-4xl mx-auto mb-4" />
          <p className="text-red-700 font-semibold">
            {error || "No data available"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaChartLine />}
        title="Exam Statistics"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Exams", to: "/admin/exam-management" },
          { label: "Statistics" },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Exam Sets</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stats.totalExamSets}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FaClipboardList className="text-blue-600 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">
                  Total Attempts
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stats.totalAttempts}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <FaUsers className="text-purple-600 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Pass Rate</p>
                <p className="text-3xl font-bold text-green-600 mt-1">
                  {stats.passRate.toFixed(1)}%
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FaTrophy className="text-green-600 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Avg Score</p>
                <p className="text-3xl font-bold text-[#009fe3] mt-1">
                  {stats.avgScore.toFixed(1)}%
                </p>
              </div>
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                <FaChartLine className="text-[#009fe3] text-xl" />
              </div>
            </div>
          </div>
        </div>

        {/* Pass/Fail Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Pass/Fail Stats */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaCheckCircle className="text-green-600" />
              Pass/Fail Distribution
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Passed
                  </span>
                  <span className="text-sm font-bold text-green-600">
                    {stats.passedAttempts} (
                    {(
                      (stats.passedAttempts / stats.totalAttempts) *
                      100
                    ).toFixed(1)}
                    %)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-green-600 h-3 rounded-full transition-all duration-500"
                    style={{
                      width: `${
                        (stats.passedAttempts / stats.totalAttempts) * 100
                      }%`,
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Failed
                  </span>
                  <span className="text-sm font-bold text-red-600">
                    {stats.failedAttempts} (
                    {(
                      (stats.failedAttempts / stats.totalAttempts) *
                      100
                    ).toFixed(1)}
                    %)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-red-600 h-3 rounded-full transition-all duration-500"
                    style={{
                      width: `${
                        (stats.failedAttempts / stats.totalAttempts) * 100
                      }%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Module Performance */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaBook className="text-blue-600" />
              Module Performance
            </h3>
            <div className="space-y-4">
              {/* Lesen */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FaBook className="text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">
                      Lesen (Reading)
                    </span>
                  </div>
                  <span className="text-sm font-bold text-blue-600">
                    {stats.moduleStats.lesen.avgPercentage.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                    style={{
                      width: `${stats.moduleStats.lesen.avgPercentage}%`,
                    }}
                  />
                </div>
              </div>

              {/* Hören */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FaHeadphones className="text-purple-600" />
                    <span className="text-sm font-medium text-gray-700">
                      Hören (Listening)
                    </span>
                  </div>
                  <span className="text-sm font-bold text-purple-600">
                    {stats.moduleStats.horen.avgPercentage.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-purple-600 h-3 rounded-full transition-all duration-500"
                    style={{
                      width: `${stats.moduleStats.horen.avgPercentage}%`,
                    }}
                  />
                </div>
              </div>

              {/* Schreiben */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FaPencilAlt className="text-green-600" />
                    <span className="text-sm font-medium text-gray-700">
                      Schreiben (Writing)
                    </span>
                  </div>
                  <span className="text-sm font-bold text-green-600">
                    {stats.moduleStats.schreiben.avgPercentage.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-green-600 h-3 rounded-full transition-all duration-500"
                    style={{
                      width: `${stats.moduleStats.schreiben.avgPercentage}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Level-wise Statistics */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            Performance by Level
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">
                    Level
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                    Attempts
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                    Passed
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                    Failed
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                    Pass Rate
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase">
                    Avg Score
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {stats.levelStats.map((levelStat, index) => (
                  <tr
                    key={levelStat.level}
                    className={`${
                      index % 2 === 1 ? "bg-gray-50" : "bg-white"
                    } hover:bg-blue-50 transition-colors`}
                  >
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-semibold bg-blue-100 text-blue-800">
                        {levelStat.level}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-semibold text-gray-900">
                        {levelStat.attempts}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-semibold text-green-600">
                        {levelStat.passed}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-semibold text-red-600">
                        {levelStat.failed}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-bold text-[#009fe3]">
                        {levelStat.passRate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-bold text-gray-900">
                        {levelStat.avgScore.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Performers & Recent Attempts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Performers */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaTrophy className="text-yellow-500" />
              Top Performers
            </h3>
            {stats.topPerformers.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">
                No data yet
              </p>
            ) : (
              <div className="space-y-3">
                {stats.topPerformers.map((performer, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">
                          {performer.studentName}
                        </div>
                        <div className="text-xs text-gray-600">
                          {performer.examSetName} ({performer.level})
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-lg text-green-600">
                        {performer.percentage.toFixed(1)}%
                      </div>
                      <div className="text-xs text-gray-600">
                        {performer.score}/{performer.maxScore}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Attempts */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Recent Submissions
            </h3>
            {stats.recentAttempts.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">
                No submissions yet
              </p>
            ) : (
              <div className="space-y-3">
                {stats.recentAttempts.map((attempt) => (
                  <div
                    key={attempt.id}
                    className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <div>
                      <div className="font-semibold text-gray-900">
                        {attempt.studentName}
                      </div>
                      <div className="text-xs text-gray-600">
                        {attempt.examSetName} ({attempt.level})
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatDate(attempt.submittedAt)}
                      </div>
                    </div>
                    <div className="text-right">
                      {attempt.isPassed ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                          <FaCheckCircle size={10} />
                          PASS
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                          <FaTimesCircle size={10} />
                          FAIL
                        </span>
                      )}
                      <div className="text-sm font-bold text-gray-900 mt-1">
                        {attempt.percentage.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminExamStatistics;
